import api from 'services/api';
import { REJECT, array, nullable, num, object, unknownRecord, unknownValue, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

// PUT /workflows/respond/:workflow_action_id — answers an action with one of its response options, which resumes the
// flow. Source of truth: nap-backend `routes/workflows.js`, which answers with the updated `workflow_actions` row. This
// contract declares what the inbox keeps of that receipt: which action of which workflow, and that it is now answered.
// It is its own type, not the save's: a different statement whose shape happens to overlap.

export interface SaveWorkflowResponse {
  readonly chosen_id: string;
}

export interface RespondedWorkflowAction {
  readonly workflow_action_id: number;
  readonly workflow_id: number;
  readonly response_options: readonly unknown[];
  readonly response: Readonly<Record<string, unknown>> | null;
}

const respondedSchema = object<RespondedWorkflowAction>({
  workflow_action_id: num(REJECT),
  workflow_id: num(REJECT),
  response_options: array(unknownValue()),
  response: nullable(unknownRecord()),
});

/**
 * PUT `/workflows/respond/:id`. Rejects on HTTP error: no response was recorded. Answers with the unreadable arm when it
 * was recorded and its receipt cannot be read, which is not a failed response (#1035): sending it again would answer
 * twice.
 */
export const putWorkflowRespond = async (workflowActionId: number, body: SaveWorkflowResponse): Promise<Received<RespondedWorkflowAction>> => {
  const res = await api.put(`/workflows/respond/${workflowActionId}`, body);
  return readResponse(res.data, { schema: respondedSchema, source: 'putWorkflowRespond' });
};
